"use client";

import Badge from "~/componentsDS/Badge";
import { QuizCTA } from "~/components/features/quiz";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Heading from "~/componentsDS/Typography/Heading";
import InteractiveTimeline, { InteractiveTimelineStep } from "~/componentsDS/InteractiveTimeline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES, QuizRoute } from "~/constants/quizRoutes";
import { cn } from "~/lib/utils";
import useLpCtaText from "~/hooks/useLpCtaText";

const CTA_TITLE = "See If Oestra® Is Right for You";

type NoticeSectionProps = {
    steps?: InteractiveTimelineStep[];
    className?: string;
    ctaLink?: QuizRoute;
    ctaText?: string;
};

const NoticeSection = ({
    steps,
    className,
    ctaLink = QUIZ_ROUTES.HORMONE_IMBALANCE,
    ctaText = CTA_TITLE,
}: NoticeSectionProps) => {
    const ctaTitle = useLpCtaText(ctaText);

    return (
        <section className={cn("pt-16 md:pt-12 pb-11 md:pb-24", className)}>
            <ElementorContainer rowGap="16px" columnGap="16px" innerClassName="flex flex-col mb-[32px]">
                <Badge className="text-[14px] md:text-[16px] mb-0 font-medium w-fit font-suisseBP px-4 py-[7px] md:px-4 md:py-[5px]">
                    Your journey
                </Badge>
                <div className="flex w-full items-start justify-between gap-8">
                    <div className="flex flex-col gap-4">
                        <Heading
                            as="h2"
                            size="sm"
                            sizes={{ md: "md" }}
                            className="text-primary500 mb-0 md:mb-0 tracking-[-1.2px]"
                        >
                            Feel it week by{" "}
                            <Heading
                                as="i"
                                font="featureDisplay"
                                weight="400"
                                className="text-[28px] md:text-[36px]"
                            >
                                week
                            </Heading>
                        </Heading>
                        <Paragraph size="lg" className="text-primary500">
                            Real changes. Backed by science. Felt by thousands of women.
                        </Paragraph>
                    </div>

                    <div className="hidden shrink-0 md:block">
                        <QuizCTA
                            variant="primary"
                            eventData={{
                                event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                                widget: "lp_weeks_improvement",
                                meta_data: {
                                    click_text: ctaText,
                                },
                            }}
                            href={ctaLink}
                            size="md"
                        >
                            <TextWithRegisteredMark text={ctaTitle} />
                        </QuizCTA>
                    </div>
                </div>
            </ElementorContainer>

            <InteractiveTimeline
                steps={steps}
                className="lg:max-w-[1920px] lg:px-0 min-[1025px]:px-12 min-[1280px]:px-16 min-[1440px]:px-20 min-[1728px]:px-[144px]"
            />
        </section>
    );
};

export default NoticeSection;
